import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Modal, toast, withReturn, type ReturnOrigin } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import { useSellSupportHours } from '../support/api';
import type { AgreementCore } from './api';

/**
 * "Sell support hours" — the invoice a SUPPORT agreement actually raises.
 *
 * Jesse, 2026-09-10: *"we don't need to invoice for spent time… we usually
 * require hours be purchased beforehand… I want to add in the number of hours
 * that will be billed to the client if it's a support agreement."*
 *
 * So this asks for HOURS, not a look back at time logged. The rate starts at
 * the agreement's hourly rate (the client's rate — everyone bills the same on
 * a client). Submitting creates one DRAFT invoice for hours × rate and the
 * PENDING block behind it; the hours land in the client's bank the moment the
 * invoice is paid, and not before. Nothing is sent from here.
 */
export function SellHoursModal({
  open,
  onOpenChange,
  agreement,
  from,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agreement: AgreementCore;
  /** Carried from the agreement page so the draft's Back stays in the client. */
  from?: ReturnOrigin | null;
}) {
  const navigate = useNavigate();
  const sell = useSellSupportHours();
  const [hours, setHours] = useState('');
  const [rate, setRate] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setHours('');
      setRate(agreement.hourlyRate !== null ? agreement.hourlyRate.toFixed(2) : '');
      setExpiresAt('');
      setError(null);
    }
  }, [open, agreement.hourlyRate]);

  const hoursNum = Number(hours);
  const rateNum = Number(rate);
  const total = Number.isFinite(hoursNum) && Number.isFinite(rateNum) && hoursNum > 0 && rateNum > 0 ? hoursNum * rateNum : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!Number.isFinite(hoursNum) || hoursNum <= 0) {
      setError('Enter the number of hours to sell.');
      return;
    }
    if (!Number.isFinite(rateNum) || rateNum <= 0) {
      setError('Enter the hourly rate — the agreement has none set.');
      return;
    }
    setError(null);
    try {
      const result = await sell.mutateAsync({
        clientId: agreement.client.id,
        agreementId: agreement.id,
        hours: hoursNum,
        rate: rateNum,
        expiresAt: expiresAt || null,
      });
      toast.success(
        `Draft #${result.invoiceNumber} created — ${result.hours} h at ${formatMoney(result.rate)}/h. The hours count once it is sent.`,
      );
      onOpenChange(false);
      navigate(withReturn(`/invoices/${result.invoiceId}`, from));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong — please try again.');
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={`Sell support hours — ${agreement.client.name}`} size="sm">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-body-sm text-grey-600">
          A draft invoice for the hours below. They go into {agreement.client.name}&rsquo;s support bank when the
          invoice is sent.
        </p>
        <Input
          label="Hours"
          type="number"
          inputMode="decimal"
          min="0.25"
          step="0.25"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          error={error ?? undefined}
          autoFocus
          placeholder="e.g. 20"
        />
        <Input
          label="Rate per hour ($)"
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          hint={
            agreement.hourlyRate !== null
              ? `The agreement's rate — ${formatMoney(agreement.hourlyRate)}/h. Change it here for this sale only.`
              : 'This agreement has no hourly rate set; enter the client’s rate.'
          }
        />
        <Input
          label="Hours expire on"
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          hint="Optional. Leave blank and the hours never expire."
        />
        <div className="flex items-center justify-between gap-3 pt-2">
          <span className="num text-body-sm text-secondary">
            {total !== null ? (
              <>
                Total <strong>{formatMoney(total)}</strong>
              </>
            ) : (
              <span className="text-grey-400">Total —</span>
            )}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={sell.isPending}>
              Create draft invoice
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
